// Loading States Component Library
// Comprehensive loading states for all onboarding and workspace components

import { AlertCircle, CheckCircle, Loader2, XCircle } from 'lucide-react';
import React from 'react';
import { cn } from '../../utils/cn';

// Base loading spinner
interface LoadingSpinnerProps {
  size?: 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
}

export const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({ 
  size = 'md', 
  className 
}) => {
  const sizeClasses = {
    sm: 'h-4 w-4',
    md: 'h-6 w-6',
    lg: 'h-8 w-8',
    xl: 'h-12 w-12'
  };

  return (
    <Loader2 
      className={cn(
        'animate-spin text-primary',
        sizeClasses[size],
        className
      )} 
    />
  );
};

// Loading overlay
interface LoadingOverlayProps {
  isVisible: boolean;
  message?: string;
  className?: string;
}

export const LoadingOverlay: React.FC<LoadingOverlayProps> = ({ 
  isVisible, 
  message = 'Loading...', 
  className 
}) => {
  if (!isVisible) return null;

  return (
    <div className={cn(
      'fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50',
      className
    )}>
      <div className="bg-white rounded-lg p-6 text-center shadow-lg">
        <LoadingSpinner size="lg" className="mx-auto mb-4" />
        <p className="text-sm text-gray-600">{message}</p>
      </div>
    </div>
  );
};

// Loading button
interface LoadingButtonProps {
  loading: boolean;
  children: React.ReactNode;
  className?: string;
  disabled?: boolean;
  onClick?: () => void;
  variant?: 'primary' | 'secondary' | 'destructive';
  size?: 'sm' | 'md' | 'lg';
}

export const LoadingButton: React.FC<LoadingButtonProps> = ({
  loading,
  children,
  className,
  disabled,
  onClick,
  variant = 'primary',
  size = 'md'
}) => {
  const baseClasses = 'inline-flex items-center justify-center rounded-md font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none';
  
  const variantClasses = {
    primary: 'bg-primary text-primary-foreground hover:bg-primary/90',
    secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
    destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90'
  };

  const sizeClasses = {
    sm: 'h-9 px-3 text-sm',
    md: 'h-10 py-2 px-4',
    lg: 'h-11 px-8'
  };

  return (
    <button
      className={cn(
        baseClasses,
        variantClasses[variant],
        sizeClasses[size],
        className
      )}
      disabled={disabled || loading}
      onClick={onClick}
    >
      {loading && <LoadingSpinner size="sm" className="mr-2" />}
      {children}
    </button>
  );
};

// Loading card
interface LoadingCardProps {
  title?: string;
  message?: string;
  className?: string;
}

export const LoadingCard: React.FC<LoadingCardProps> = ({ 
  title = 'Loading...', 
  message = 'Please wait while we load your data',
  className 
}) => {
  return (
    <div className={cn(
      'bg-white rounded-lg border border-gray-200 p-6 text-center',
      className
    )}>
      <LoadingSpinner size="lg" className="mx-auto mb-4" />
      <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
      <p className="text-sm text-gray-600">{message}</p>
    </div>
  );
};

// Loading skeleton
interface LoadingSkeletonProps {
  className?: string;
  lines?: number;
}

export const LoadingSkeleton: React.FC<LoadingSkeletonProps> = ({ 
  className, 
  lines = 3 
}) => {
  return (
    <div className={cn('space-y-3', className)}>
      {Array.from({ length: lines }).map((_, i) => (
        <div
          key={i}
          className={cn(
            'h-4 bg-gray-200 rounded animate-pulse',
            i === lines - 1 ? 'w-3/4' : 'w-full'
          )}
        />
      ))}
    </div>
  );
};

// Status indicator
interface StatusIndicatorProps {
  status: 'loading' | 'success' | 'error' | 'warning';
  message?: string;
  className?: string;
}

export const StatusIndicator: React.FC<StatusIndicatorProps> = ({ 
  status, 
  message, 
  className 
}) => {
  const statusConfig = {
    loading: { icon: Loader2, color: 'text-blue-600', bg: 'bg-blue-50' },
    success: { icon: CheckCircle, color: 'text-green-600', bg: 'bg-green-50' },
    error: { icon: XCircle, color: 'text-red-600', bg: 'bg-red-50' },
    warning: { icon: AlertCircle, color: 'text-yellow-600', bg: 'bg-yellow-50' }
  };

  const config = statusConfig[status];
  const IconComponent = config.icon;

  return (
    <div className={cn(
      'flex items-center space-x-2 p-3 rounded-md',
      config.bg,
      className
    )}>
      <IconComponent className={cn(
        'h-4 w-4',
        config.color,
        status === 'loading' ? 'animate-spin' : ''
      )} />
      {message && (
        <span className={cn('text-sm font-medium', config.color)}>
          {message}
        </span>
      )}
    </div>
  );
};

// Progress loader
interface ProgressLoaderProps {
  progress: number;
  message?: string;
  className?: string;
}

export const ProgressLoader: React.FC<ProgressLoaderProps> = ({ 
  progress, 
  message, 
  className 
}) => {
  return (
    <div className={cn('space-y-3', className)}>
      {message && (
        <p className="text-sm text-gray-600">{message}</p>
      )}
      <div className="w-full bg-gray-200 rounded-full h-2">
        <div 
          className="bg-primary h-2 rounded-full transition-all duration-300 ease-out"
          style={{ width: `${Math.min(100, Math.max(0, progress))}%` }}
        />
      </div>
      <div className="flex justify-between text-xs text-gray-500">
        <span>0%</span>
        <span>{Math.round(progress)}%</span>
        <span>100%</span>
      </div>
    </div>
  );
};

// Inline loading
interface InlineLoadingProps {
  message?: string;
  className?: string;
}

export const InlineLoading: React.FC<InlineLoadingProps> = ({ 
  message = 'Loading...', 
  className 
}) => {
  return (
    <div className={cn('flex items-center space-x-2', className)}>
      <LoadingSpinner size="sm" />
      <span className="text-sm text-gray-600">{message}</span>
    </div>
  );
};

// Page loading
interface PageLoadingProps {
  title?: string;
  message?: string;
  className?: string;
}

export const PageLoading: React.FC<PageLoadingProps> = ({ 
  title = 'Loading...', 
  message = 'Please wait while we prepare your workspace',
  className 
}) => {
  return (
    <div className={cn(
      'min-h-screen flex items-center justify-center bg-gray-50',
      className
    )}>
      <div className="text-center">
        <LoadingSpinner size="xl" className="mx-auto mb-6" />
        <h1 className="text-2xl font-bold text-gray-900 mb-2">{title}</h1>
        <p className="text-gray-600">{message}</p>
      </div>
    </div>
  );
};

// Workspace setup loading
interface WorkspaceSetupLoadingProps {
  message?: string;
  statusMessage?: string;
  className?: string;
}

export const WorkspaceSetupLoading: React.FC<WorkspaceSetupLoadingProps> = ({
  message = 'Setting up organisation and app...',
  statusMessage,
  className
}) => {
  return (
    <div className={cn(
      'fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50',
      className
    )}>
      <div className="bg-white rounded-lg p-8 text-center shadow-xl max-w-md mx-4">
        <LoadingSpinner size="xl" className="mx-auto mb-6" />
        <h2 className="text-xl font-semibold text-gray-900 mb-2">{message}</h2>
        {statusMessage && (
          <p className="text-sm text-gray-600 mt-2">{statusMessage}</p>
        )}
        <div className="mt-6">
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-primary h-2 rounded-full animate-pulse"
              style={{ width: '60%' }}
            />
          </div>
        </div>
        <p className="text-xs text-gray-500 mt-4">This may take a few moments...</p>
      </div>
    </div>
  );
};
